/**
 * File Formatting Utilities
 *
 * Helpers for file size formatting, type identification, and validation.
 */

// =============================================================================
// Constants
// =============================================================================

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const ALLOWED_MIME_TYPES = [
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Text
  'text/plain',
  'text/csv',
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

// Generic MIME types that require extension validation
// (some browsers report Office files as these)
const GENERIC_MIME_TYPES = [
  'application/octet-stream',
  'application/zip',
  'application/x-zip-compressed',
];

// Valid file extensions for fallback validation
const VALID_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv',
  '.jpg', '.jpeg', '.png', '.gif', '.webp',
];

// User-friendly extension list for UI display
export const ALLOWED_EXTENSIONS = '.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .csv, .jpg, .png, .gif';

// =============================================================================
// File Size Formatting
// =============================================================================

/**
 * Format bytes to human-readable file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

// =============================================================================
// File Type Helpers
// =============================================================================

type FileIconType = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'image' | 'text' | 'file';

/**
 * Get icon type based on MIME type
 */
export function getFileIconType(mimeType: string): FileIconType {
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'powerpoint';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('text/')) return 'text';
  return 'file';
}

/**
 * Get color class for file icon
 */
export function getFileIconColor(iconType: FileIconType): string {
  const colors: Record<FileIconType, string> = {
    pdf: 'text-red-500',
    word: 'text-blue-500',
    excel: 'text-green-500',
    powerpoint: 'text-orange-500',
    image: 'text-purple-500',
    text: 'text-gray-500',
    file: 'text-gray-400',
  };
  return colors[iconType];
}

// =============================================================================
// Validation
// =============================================================================

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot >= 0 ? filename.slice(lastDot).toLowerCase() : '';
}

/**
 * Check if file type is valid (by MIME type or extension fallback)
 */
function isValidFileType(file: File): boolean {
  // Check if MIME type is explicitly allowed
  if (ALLOWED_MIME_TYPES.includes(file.type)) {
    return true;
  }

  // For generic MIME types, validate by extension instead
  // (some browsers report .xlsx, .docx as application/octet-stream or application/zip)
  if (GENERIC_MIME_TYPES.includes(file.type)) {
    const ext = getFileExtension(file.name);
    return VALID_EXTENSIONS.includes(ext);
  }

  return false;
}

/**
 * Validate a file for upload
 */
export function validateFile(file: File): FileValidationResult {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size must be less than ${formatFileSize(MAX_FILE_SIZE)}`,
    };
  }

  // Check file type
  if (!isValidFileType(file)) {
    return {
      valid: false,
      error: `File type not allowed. Accepted: ${ALLOWED_EXTENSIONS}`,
    };
  }

  return { valid: true };
}
